/**
 * Per-tick movement resolution. Consumes input state, mutates the player.
 * See CLAUDE.md architecture rules 1, 2, 4 (fixed tick + integer grid +
 * headless sim, no PixiJS).
 *
 * Two-part player model:
 *   - LEGS (WASD, world-absolute 8-way) are the movement vector. The player
 *     carries a FREE floating-point position `(fx, fy)` in tile units — the
 *     source of truth. Each tick we integrate a velocity (leg direction ×
 *     stance speed) into `(fx, fy)` continuously, with per-axis collision so
 *     walls block and corners slide. The logical integer tile `(x, y)` is
 *     derived as `round(fx), round(fy)`, keeping collision / FOV / sound / AI
 *     on the grid (arch rule 2). When no move key is held the player STOPS,
 *     then after `CONFIG.move.snapDelaySec` the float eases to the nearest tile
 *     centre so you come to rest ON the grid a brief moment after stopping.
 *   - TORSO (arrow keys, leg-relative 8-way) is where the player looks/aims —
 *     stored in `facingDx/facingDy`, which the vision cone, weapon aim, focus,
 *     and camera all read. Resolved by `resolveTorso` for the UNFOCUSED case;
 *     while focus-locked, main.ts points the torso at the target instead.
 *
 * `visualX/Y` mirror `(fx, fy)`; the renderer lerps prev→current with the
 * FixedLoop's alpha. Guards still use the tile-stepping `moveProgress` model
 * (see guard.ts).
 */

import { CONFIG } from '../config';
import { isBlindStunned } from './blind';
import {
  composeTorsoFacing,
  getStance,
  isAiming,
  isFireHeld,
  screenToWorldByHeading,
  torsoOffsetFromArrows,
  type InputState,
} from './input';
import { inBounds, isBlocked, tileAt, type MapData } from './map';
import { speedFor, type Player } from './player';
import { footstepLoudness } from './soundQueue';
import { isCoverAnchor, isCoverTile } from './tiles';

/** Emit callback used by movement to push a sound event into the queue.
 *  `emitter` distinguishes the player from a specific guard so awareness
 *  can skip self-emitted events. */
export type EmitSound = (x: number, y: number, loudness: number, emitter: number | 'player') => void;

/**
 * Free-fly SPECTATOR movement for a dead player. The corpse becomes a roaming
 * observer: WASD drifts it anywhere across the map — through walls, ignoring all
 * collision — so you can watch the sim play out after you die. It has NO vision
 * of its own (the caller keeps `fovTiles` empty) and no look control (facing is
 * frozen), so this only integrates position. Movement is camera-relative like
 * the live player (W = up-on-screen), continuous (no 8-way snap) for smooth
 * panning, and clamped to the map bounds.
 */
export function updateSpectator(
  player: Player,
  input: InputState,
  map: MapData,
  tickDt: number,
  cameraHeading = 0,
): void {
  let sx = 0;
  let sy = 0;
  if (input.held.has('KeyW')) sy -= 1;
  if (input.held.has('KeyS')) sy += 1;
  if (input.held.has('KeyA')) sx -= 1;
  if (input.held.has('KeyD')) sx += 1;
  if (sx !== 0 || sy !== 0) {
    // Rotate the screen intent into world space by the camera heading (kept
    // continuous, not axis-snapped, so diagonals glide).
    const c = Math.cos(cameraHeading);
    const s = Math.sin(cameraHeading);
    const wx = c * sx + s * sy;
    const wy = -s * sx + c * sy;
    const len = Math.hypot(wx, wy) || 1;
    const speed = CONFIG.spectator.speed;
    player.fx = Math.max(0, Math.min(map.width - 1, player.fx + (wx / len) * speed * tickDt));
    player.fy = Math.max(0, Math.min(map.height - 1, player.fy + (wy / len) * speed * tickDt));
  }
  player.x = Math.round(player.fx);
  player.y = Math.round(player.fy);
  player.moveIntentDx = sx;
  player.moveIntentDy = sy;
  player.moveProgress = 0;
  player.visualX = player.fx;
  player.visualY = player.fy;
}

export function updatePlayer(
  player: Player,
  input: InputState,
  map: MapData,
  tickDt: number,
  // Current camera rotation (radians). WASD is camera-relative: a key's world
  // direction is derived through this heading at the moment the held-key
  // combination changes, then latched (see the leg block below).
  cameraHeading = 0,
  emitSound?: EmitSound,
): void {
  // Stance is set every tick from input, then forced to walk while exhausted.
  let stance = getStance(input);
  if (stance === 'run' && player.stamina <= 0) player.exhausted = true;
  if (
    player.exhausted &&
    player.stamina >= CONFIG.stamina.max * CONFIG.stamina.recoverThreshold
  ) {
    player.exhausted = false;
  }
  if (stance === 'run' && player.exhausted) stance = 'walk';
  // In cover: auto-crouch (tucked behind the wall, hidden) UNLESS peeking.
  // Peeking stands the player up (exposed) to see/shoot over the cover:
  //   - aiming (arrow held) or firing (Space), or
  //   - holding C to deliberately rise up out of the tuck (release → tuck back).
  // While C is held you stay LOCKED to cover (can't be pushed off it — see
  // coverSlide). The crouch-toggle latch is neutralised in cover so its state
  // can't leak out when you leave. Overrides the input stance.
  const coverLock = player.inCover && input.held.has('KeyC');
  if (player.inCover) {
    input.crouchLatched = false;
    // Two ways to peek out of the tuck, and they read differently:
    //   - OVER THE TOP (deliberately rise: aim / fire / hold C) → STAND UP.
    //   - AROUND THE SIDE (slid PAST the cover's end into the open) → stay
    //     CROUCHED; only the torso leans out past the cover edge.
    // Simply sliding ALONG the wall (still covered) keeps you tucked.
    const coveredHere = isCoverAnchor(tileAt(map, player.x + player.coverDx, player.y + player.coverDy));
    const overTop = isAiming(input) || isFireHeld(input) || input.held.has('KeyC');
    const sidePeek = !coveredHere;
    stance = overTop ? 'walk' : 'crouch';
    player.peekingOut = overTop || sidePeek;

    // Torso-lean direction for a crouched side-peek: the tangent (along-wall)
    // direction you leaned, so the torso pokes out past the cover's end.
    let leanDx = 0, leanDy = 0;
    if (sidePeek && !overTop) {
      const tangentIsX = player.coverDy !== 0; // vertical cover → horizontal lean
      if (tangentIsX) leanDx = Math.sign(player.legDx) || Math.sign(player.moveDx);
      else leanDy = Math.sign(player.legDy) || Math.sign(player.moveDy);
    }
    player.coverLeanDx = leanDx;
    player.coverLeanDy = leanDy;

    // Looking out: when peeking you look BEYOND the obstruction (the cover
    // normal) plus AROUND the corner (the lean), so your vision/aim clears the
    // cover you're snapped onto. resolveTorsoHold (focus is armed while peeking)
    // preserves this unless you aim with the arrows.
    if (player.peekingOut) {
      player.facingDx = clampUnit(player.coverDx + leanDx);
      player.facingDy = clampUnit(player.coverDy + leanDy);
    }
  } else {
    player.peekingOut = false;
    player.coverLeanDx = 0;
    player.coverLeanDy = 0;
  }
  player.stance = stance;

  // --- Legs (WASD → camera-relative, latched) ------------------------------
  // WASD is relative to the current VIEW: W = up-on-screen (forward, toward
  // where the torso/camera points), S = back, A/D = strafe. Each held key's
  // WORLD direction is computed through the camera heading and LATCHED the
  // moment the held-key combination changes, then held while those keys stay
  // down — so a held key walks a straight world line even as the camera
  // rotates (no polar "W = north" feel, and no spiral from chasing the
  // rotating camera). Changing the keys re-bases onto the current view; and
  // because the camera trails your legs when you're not aiming, "up" becomes
  // your travel direction as you walk — i.e. the direction resets to where
  // you're heading. `legDx/legDy` is the retained orientation (kept while idle
  // as the torso's reference frame); `moveIntent` is 0/0 when not moving.
  let sx = 0;
  let sy = 0;
  if (input.held.has('KeyW')) sy -= 1;
  if (input.held.has('KeyS')) sy += 1;
  if (input.held.has('KeyA')) sx -= 1;
  if (input.held.has('KeyD')) sx += 1;
  const sig =
    `${input.held.has('KeyW') ? 'W' : ''}${input.held.has('KeyA') ? 'A' : ''}` +
    `${input.held.has('KeyS') ? 'S' : ''}${input.held.has('KeyD') ? 'D' : ''}`;
  if (sig !== player.legKeySig) {
    // The held combination changed → re-latch the leg direction onto the
    // current camera view. A zero net (no keys, or opposing keys) leaves the
    // last orientation intact so the torso reference / legs glyph don't snap.
    const w = screenToWorldByHeading(sx, sy, cameraHeading);
    if (w.dx !== 0 || w.dy !== 0) {
      player.legDx = w.dx;
      player.legDy = w.dy;
    }
    player.legKeySig = sig;
  }
  // Net-zero screen intent (no keys, or W+S / A+D cancelling) = not moving.
  // A flash-blind stun also freezes movement for its first second (dizzy).
  const moving = (sx !== 0 || sy !== 0) && !isBlindStunned(player);
  if (moving) {
    player.moveIntentDx = player.legDx;
    player.moveIntentDy = player.legDy;
    player.restTimer = 0;
  } else {
    player.moveIntentDx = 0;
    player.moveIntentDy = 0;
    player.restTimer += tickDt;
  }

  // --- Free-float integration ----------------------------------------------
  // Did the player cover any ground this tick? Used for stamina + footsteps.
  let locomoted = false;

  // In cover: cover-relative movement (A/D slide, S off, W crawl over a half
  // wall). May clear inCover (S / crawl-complete), in which case normal movement
  // resumes this tick.
  if (player.inCover) moveInCover(player, input, map, tickDt, coverLock);

  const vaulting = (player.vaultX ?? -1) >= 0;
  if (player.inCover) {
    // Handled by coverSlide; skip the free-float integration below.
  } else if (vaulting) {
    // A committed sprint vault: glide straight over the half wall to the far
    // tile, ignoring its collision. Runs to completion even if keys are released.
    updateVault(player, tickDt);
    locomoted = true;
  } else if (moving) {
    // Instantaneous velocity (no accel ramp — that's what makes it snappy).
    // Normalise the leg direction so a diagonal isn't √2 faster than a cardinal.
    const len = Math.hypot(player.moveIntentDx, player.moveIntentDy) || 1;
    const speed = speedFor(player);
    const vx = (player.moveIntentDx / len) * speed;
    const vy = (player.moveIntentDy / len) * speed;
    const beforeX = player.fx;
    const beforeY = player.fy;
    // Integrate each axis independently so a blocked axis still slides along
    // the open one (wall-sliding). Resolve X first, then Y against the tile
    // X just landed on, so a diagonal can't clip through a blocked corner.
    player.fx = moveAxis(player.fx, player.fy, vx * tickDt, map, true);
    player.fy = moveAxis(player.fy, player.fx, vy * tickDt, map, false);
    if (player.fx !== beforeX || player.fy !== beforeY) locomoted = true;
    // Keep the step-direction fields loosely in sync for anything that reads
    // them (debug overlay, animation): sign of current velocity.
    player.moveDx = Math.sign(vx);
    player.moveDy = Math.sign(vy);
    // Sprinting straight into a half wall auto-vaults over it: if we're running
    // up against a half wall with open floor beyond, commit a vault this tick
    // (executed by the branch above from next tick on). The scramble over is
    // loud — it emits a sound that draws nearby guards.
    if (stance === 'run' && tryStartVault(player, map)) {
      // A vault is an explosive sprint action — costs a burst of stamina.
      player.stamina = Math.max(0, player.stamina - CONFIG.stamina.vaultCost);
      emitSound?.(player.x, player.y, CONFIG.sound.loudness.vault, 'player');
    }
  } else {
    // No move key held: stop. After a brief delay, ease the free float toward
    // the nearest tile centre so the player settles ON the grid rather than
    // resting between tiles. The target tile is `round(fx/fy)` — the current
    // logical tile, which is already collision-valid — and easing stays within
    // that cell (|Δ| < 0.5), so rounding never changes and no wall check is
    // needed.
    player.moveDx = 0;
    player.moveDy = 0;
    if (player.restTimer >= CONFIG.move.snapDelaySec) {
      const tx = Math.round(player.fx);
      const ty = Math.round(player.fy);
      const k = Math.min(1, CONFIG.move.snapRate * tickDt);
      player.fx += (tx - player.fx) * k;
      player.fy += (ty - player.fy) * k;
      if (Math.abs(tx - player.fx) < CONFIG.move.snapEpsilon) player.fx = tx;
      if (Math.abs(ty - player.fy) < CONFIG.move.snapEpsilon) player.fy = ty;
    }
  }

  // Derive the logical integer tile from the float position. Everything
  // downstream (collision, FOV, sound, blood, AI, combat) reads (x, y).
  const prevTileX = player.x;
  const prevTileY = player.y;
  player.x = Math.round(player.fx);
  player.y = Math.round(player.fy);

  // Emit a footstep whenever we cross into a new tile — the grid-space analog
  // of the old per-step emission, so sound cadence tracks distance travelled.
  if (emitSound && (player.x !== prevTileX || player.y !== prevTileY)) {
    const loud = footstepLoudness(player.stance, CONFIG.sound.loudness);
    emitSound(player.x, player.y, loud, 'player');
  }

  // Stamina: drain while actually running (and moving), regen otherwise.
  const runningNow = stance === 'run' && moving && locomoted;
  if (runningNow) {
    player.stamina = Math.max(0, player.stamina - CONFIG.stamina.drainPerSec * tickDt);
  } else {
    player.stamina = Math.min(
      CONFIG.stamina.max,
      player.stamina + CONFIG.stamina.regenPerSec * tickDt,
    );
  }

  // moveProgress is unused by the free-float player; keep it zeroed so the
  // shared entity interpolation (guards) and debug readouts stay sane.
  player.moveProgress = 0;
  player.visualX = player.fx;
  player.visualY = player.fy;
}

/**
 * Point the torso (`facingDx/facingDy`) for the UNFOCUSED case. The arrow keys
 * aim relative to the legs: with no arrow held the torso faces the legs (snaps
 * back); with an arrow held it offsets from the legs' orientation (see
 * `composeTorsoFacing`). The change is instant/discrete — the camera's heading
 * lerp supplies the smooth swing. Call after `updatePlayer` when the player is
 * NOT focus-locked; while focused, main.ts aims the torso at the target.
 */
export function resolveTorso(player: Player, input: InputState): void {
  const off = torsoOffsetFromArrows(input);
  const t = composeTorsoFacing(player.legDx, player.legDy, off.dx, off.dy);
  player.facingDx = t.dx;
  player.facingDy = t.dy;
}

/**
 * Torso aim for PRIMED focus mode (focus on, no target). Unlike `resolveTorso`,
 * the torso is LOCKED to its current direction and does NOT snap back to the
 * legs — so the player can move (legs) freely while holding a fixed aim/watch
 * direction. Holding an arrow re-aims it (leg-relative); releasing holds the
 * last direction.
 */
export function resolveTorsoHold(player: Player, input: InputState): void {
  const off = torsoOffsetFromArrows(input);
  if (off.dx === 0 && off.dy === 0) return; // no arrow → hold current facing
  const t = composeTorsoFacing(player.legDx, player.legDy, off.dx, off.dy);
  player.facingDx = t.dx;
  player.facingDy = t.dy;
}

/** How far into the current tile (toward the wall) the player must be before a
 *  sprint into a half wall triggers the vault — so it fires as you reach it,
 *  not from a tile away. */
const VAULT_TRIGGER_FRAC = 0.15;

/**
 * If the player is sprinting straight at a low-cover tile (half wall OR cubicle
 * partition) with open floor on the far side, commit a vault: store the far tile
 * in `vaultX/vaultY`. Checks the cardinal(s) of the current move intent (X first,
 * then Y). Returns true if a vault was started this call (so the caller can emit
 * the vault sound).
 */
function tryStartVault(player: Player, map: MapData): boolean {
  const mdx = Math.sign(player.moveIntentDx);
  const mdy = Math.sign(player.moveIntentDy);
  const cands: Array<readonly [number, number]> = [];
  if (mdx !== 0) cands.push([mdx, 0]);
  if (mdy !== 0) cands.push([0, mdy]);
  for (const [dx, dy] of cands) {
    // Only if we're actually pressed up against the cover on this axis.
    const towardWall = dx !== 0 ? (player.fx - player.x) * dx : (player.fy - player.y) * dy;
    if (towardWall < VAULT_TRIGGER_FRAC) continue;
    if (!isCoverTile(tileAt(map, player.x + dx, player.y + dy))) continue; // half wall / cubicle
    const farX = player.x + 2 * dx;
    const farY = player.y + 2 * dy;
    if (!inBounds(map, farX, farY) || isBlocked(map, farX, farY)) continue;
    player.vaultX = farX;
    player.vaultY = farY;
    return true;
  }
  return false;
}

/**
 * Advance a committed sprint vault: glide the float position toward the stored
 * far tile at run speed (straight over the half wall — no collision check),
 * clearing the vault when it arrives.
 */
function updateVault(player: Player, tickDt: number): void {
  const tx = player.vaultX ?? -1;
  const ty = player.vaultY ?? -1;
  if (tx < 0 || ty < 0) return;
  const step = CONFIG.speed.run * tickDt;
  player.fx += Math.sign(tx - player.fx) * Math.min(step, Math.abs(tx - player.fx));
  player.fy += Math.sign(ty - player.fy) * Math.min(step, Math.abs(ty - player.fy));
  const vdx = Math.sign(tx - player.fx);
  const vdy = Math.sign(ty - player.fy);
  if (vdx !== 0 || vdy !== 0) {
    player.moveDx = vdx;
    player.moveDy = vdy;
    player.legDx = vdx || player.legDx;
    player.legDy = vdy || player.legDy;
  }
  if (Math.abs(player.fx - tx) < 0.03 && Math.abs(player.fy - ty) < 0.03) {
    player.fx = tx;
    player.fy = ty;
    player.vaultX = -1;
    player.vaultY = -1;
  }
}

/**
 * Move one axis of a float position by `delta`, blocked by walls. `pos` is the
 * moving axis; `other` is the fixed axis (already resolved). `isX` selects
 * which coordinate feeds `isBlocked`. If the destination would round into a
 * blocked tile, clamp just short of the boundary into it (so the player rests
 * flush against the wall) rather than entering it.
 */
function moveAxis(pos: number, other: number, delta: number, map: MapData, isX: boolean): number {
  if (delta === 0) return pos;
  const target = pos + delta;
  const curTile = Math.round(pos);
  const targetTile = Math.round(target);
  const otherTile = Math.round(other);
  if (targetTile !== curTile) {
    const blocked = isX
      ? isBlocked(map, targetTile, otherTile)
      : isBlocked(map, otherTile, targetTile);
    if (blocked) {
      // Rest flush against the wall: the outer edge of the current tile,
      // a hair short of the 0.5 boundary so round() stays on curTile.
      return curTile + Math.sign(delta) * 0.4999;
    }
  }
  return target;
}

/**
 * Cover-relative movement. The cover normal `coverDx/coverDy` points at the wall
 * ("forward"), so the player is oriented facing the cover:
 *   - A / D slide LEFT / RIGHT along the wall. Sliding leans you out (you're
 *     exposed while moving — the stance override stands you up); going idle
 *     snaps you back flush into cover (tucked, hidden), including reeling back
 *     in from a lean past the wall's end.
 *   - S backs off the cover (clears `inCover`) — unless `locked` (holding C).
 *   - W crawls OVER a half wall to the tile on its far side (full walls can't be
 *     climbed). Suppressed while sliding or locked.
 * The normal axis is always pinned to the wall-border tile so you hug the wall.
 */
function moveInCover(player: Player, input: InputState, map: MapData, tickDt: number, locked: boolean): void {
  const nDx = player.coverDx;
  const nDy = player.coverDy;
  const aHeld = input.held.has('KeyA');
  const dHeld = input.held.has('KeyD');
  const wHeld = input.held.has('KeyW');
  const sHeld = input.held.has('KeyS');

  // S → back off the cover (unless locked in place with C).
  if (sHeld && !locked) {
    player.inCover = false;
    return;
  }

  // W → crawl over a half wall to the far side. Once started it commits to a
  // fixed target (so the target doesn't drift as the player moves), even if W is
  // released. Not started while sliding or locked.
  const crawling = (player.coverCrawlX ?? -1) >= 0;
  if (crawling || (wHeld && !aHeld && !dHeld && !locked)) {
    let tx = player.coverCrawlX ?? -1;
    let ty = player.coverCrawlY ?? -1;
    if (!crawling) {
      const coverT = tileAt(map, player.x + nDx, player.y + nDy);
      const farX = player.x + 2 * nDx;
      const farY = player.y + 2 * nDy;
      // "Half cover" you can climb over = cubicle partitions + half walls (not
      // full walls). The far side must be walkable.
      if (!isCoverTile(coverT) || !inBounds(map, farX, farY) || isBlocked(map, farX, farY)) {
        return; // full wall or blocked far side → can't crawl over
      }
      tx = farX;
      ty = farY;
      player.coverCrawlX = tx;
      player.coverCrawlY = ty;
    }
    const step = CONFIG.speed.crouch * tickDt; // crawl slowly, straight over
    player.fx += Math.sign(tx - player.fx) * Math.min(step, Math.abs(tx - player.fx));
    player.fy += Math.sign(ty - player.fy) * Math.min(step, Math.abs(ty - player.fy));
    player.moveDx = nDx;
    player.moveDy = nDy;
    player.legDx = nDx;
    player.legDy = nDy;
    if (Math.abs(player.fx - tx) < 0.03 && Math.abs(player.fy - ty) < 0.03) {
      player.fx = tx;
      player.fy = ty;
      player.coverCrawlX = -1;
      player.coverCrawlY = -1;
      player.inCover = false; // crossed to the far side
      // Stay low after crawling over — you were crouched/crawling, so don't pop
      // straight up to standing on the far side.
      input.crouchLatched = true;
    }
    return;
  }

  // Pin the normal axis (hug the wall border).
  if (nDx !== 0) player.fx = player.x;
  if (nDy !== 0) player.fy = player.y;

  // A / D → slide along the tangent (parallel to the wall). Facing the wall,
  // A = left, D = right.
  const aDx = nDy, aDy = -nDx;
  const dDx = -nDy, dDy = nDx;
  let sDx = 0, sDy = 0;
  if (aHeld) { sDx += aDx; sDy += aDy; }
  if (dHeld) { sDx += dDx; sDy += dDy; }
  const tangentIsX = nDy !== 0;

  if (sDx !== 0 || sDy !== 0) {
    // Sliding — moving is the peek (you can slide past the wall's end into the
    // open, leaning around the corner; only solid walls stop you).
    player.restTimer = 0;
    const speed = speedFor(player);
    if (tangentIsX) {
      const dir = Math.sign(sDx);
      player.fx = coverSlideAxis(player.fx, player.y, dir * speed * tickDt, map, true, nDx, nDy);
      player.moveDx = dir;
      player.moveDy = 0;
    } else {
      const dir = Math.sign(sDy);
      player.fy = coverSlideAxis(player.fy, player.x, dir * speed * tickDt, map, false, nDx, nDy);
      player.moveDx = 0;
      player.moveDy = dir;
    }
    player.legDx = Math.sign(sDx);
    player.legDy = Math.sign(sDy);
  } else {
    // Idle → snap back flush into cover (tuck), reeling in from any lean.
    player.moveDx = 0;
    player.moveDy = 0;
    player.restTimer += tickDt;
    if (player.restTimer >= CONFIG.move.snapDelaySec) coverSnapBack(player, map, tickDt, tangentIsX);
  }
}

/** Ease the player's tangent position back onto the nearest covered tile. If
 *  currently leaned out past the wall's end (tile not covered), step back one
 *  tile toward the covered side. */
function coverSnapBack(player: Player, map: MapData, tickDt: number, tangentIsX: boolean): void {
  const nDx = player.coverDx;
  const nDy = player.coverDy;
  const k = Math.min(1, CONFIG.move.snapRate * tickDt);
  const coveredHere = isCoverAnchor(tileAt(map, player.x + nDx, player.y + nDy));
  if (tangentIsX) {
    const target = coveredHere ? Math.round(player.fx) : player.x + coveredTangentStep(player, map, true);
    player.fx += (target - player.fx) * k;
    if (Math.abs(target - player.fx) < CONFIG.move.snapEpsilon) player.fx = target;
    player.fy = player.y;
  } else {
    const target = coveredHere ? Math.round(player.fy) : player.y + coveredTangentStep(player, map, false);
    player.fy += (target - player.fy) * k;
    if (Math.abs(target - player.fy) < CONFIG.move.snapEpsilon) player.fy = target;
    player.fx = player.x;
  }
}

/**
 * Slide one tangent axis while in cover. Solid walls stop you; you may slide
 * freely along covered tiles and lean exactly ONE tile past the covered
 * segment's end (peek around the corner) — but no further into the open.
 */
function coverSlideAxis(
  pos: number,
  other: number,
  delta: number,
  map: MapData,
  isX: boolean,
  nDx: number,
  nDy: number,
): number {
  if (delta === 0) return pos;
  const target = pos + delta;
  const curTile = Math.round(pos);
  const targetTile = Math.round(target);
  const otherTile = Math.round(other);
  if (targetTile !== curTile) {
    const tx = isX ? targetTile : otherTile;
    const ty = isX ? otherTile : targetTile;
    const cx = isX ? curTile : otherTile;
    const cy = isX ? otherTile : curTile;
    const coveredTarget = isCoverAnchor(tileAt(map, tx + nDx, ty + nDy));
    const coveredCur = isCoverAnchor(tileAt(map, cx + nDx, cy + nDy));
    // Allow the step if walkable AND (the target is covered, OR we're stepping
    // one tile off the end from a covered tile — the corner lean). Otherwise clamp.
    if (isBlocked(map, tx, ty) || !(coveredTarget || coveredCur)) {
      return curTile + Math.sign(delta) * 0.4999;
    }
  }
  return target;
}

/** Which ±1 tangent step lands on a walkable tile that still has the cover
 *  beside it (i.e. back into the covered segment). 0 if neither. */
function coveredTangentStep(player: Player, map: MapData, tangentIsX: boolean): number {
  const nDx = player.coverDx;
  const nDy = player.coverDy;
  for (const d of [1, -1] as const) {
    const tx = player.x + (tangentIsX ? d : 0);
    const ty = player.y + (tangentIsX ? 0 : d);
    if (!isBlocked(map, tx, ty) && isCoverAnchor(tileAt(map, tx + nDx, ty + nDy))) return d;
  }
  return 0;
}

/** Clamp a value to the {-1, 0, 1} direction range. */
function clampUnit(v: number): number {
  return v < 0 ? -1 : v > 0 ? 1 : 0;
}

/** Recompute a tile-stepping entity's visual position (guards). */
export function syncVisual(e: { x: number; y: number; moveDx: number; moveDy: number; moveProgress: number; visualX: number; visualY: number; }): void {
  e.visualX = e.x + e.moveDx * e.moveProgress;
  e.visualY = e.y + e.moveDy * e.moveProgress;
}
